'use client'

import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { cn, isPopulatedObject } from '@/lib/utils'
import type { StackType } from '@/app/(app)/fetchers'
import type { App, Media, StackAppsApp, AppCategoryRecommendation, Category } from '@/payload-types'

type AppStacksTypeProps = {
  stack: StackType
}

export default function AppStacksType({ stack }: AppStacksTypeProps) {
  if (!stack) return null

  const stackApps = stack.stackApps?.docs as StackAppsApp[]

  if (stackApps && stackApps.length > 0) {
    return (
      <div className="group relative mt-auto h-[280px] w-full">
        <div className="absolute bottom-0 left-1/2 flex h-full -translate-x-1/2 flex-col gap-12 transition-all duration-500 ease-in-out">
          {stackApps.slice(0, 5).map((stackApp: StackAppsApp, index: number) => {
            const app = stackApp.app as App
            const appIcon = app.icon as Media
            const appGlyph = app.glyph as Media

            const appRecs = app.categoryRecommendations?.docs as AppCategoryRecommendation[]
            const primaryCategory = appRecs?.find((rec) => rec.classification === 'primary')
              ?.category as Category

            if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
              return null
            }

            return (
              <div
                key={`${app.id}-${index}`}
                style={{ zIndex: stackApps.length - index, willChange: 'transform' }}
                className={cn(
                  'relative -mt-[120px] transition-transform duration-500 ease-in-out first:mt-0',
                  index % 2 === 0 ? 'group-hover:translate-x-20' : 'group-hover:-translate-x-20',
                )}
              >
                <div
                  className="-rotate-[35deg] skew-x-[24deg] transition-transform"
                  style={{ willChange: 'transform' }}
                >
                  <AppIcon
                    glyph={appGlyph}
                    icon={appIcon}
                    name={app.name}
                    slug={app.slug}
                    size={128}
                    background={app.brandBackgroundColor}
                    className="shadow-button"
                  />
                </div>

                {primaryCategory && (
                  <span
                    className={cn(
                      'absolute top-1/2 flex -translate-y-1/2 items-center gap-2 truncate rounded-lg border border-black/10 bg-white p-2 text-12 font-medium leading-none text-gray-950 transition-all duration-500 ease-in-out group-hover:opacity-0',
                      index % 2 === 0
                        ? 'right-full translate-x-8 md:-translate-x-14'
                        : 'left-full -translate-x-8 md:translate-x-14',
                    )}
                  >
                    <Icon
                      name={primaryCategory.icon as string}
                      size={12}
                      className="text-primary-500"
                    />
                    {primaryCategory.name}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}
